import { Fork, Invoke } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFCitizenship from '../components/RMFCitizenship/RMFCitizenship';
import RMFCrossOperatorDepositLimit from '../components/RMFCrossOperatorDepositLimit/RMFCrossOperatorDepositLimit';
import RMFDOB from '../components/RMFDOB/RMFDOB';
import RMFLegalName from '../components/RMFLegalName/RMFLegalName';
import RMFPhone from '../components/RMFPhone/RMFPhone';
import RMFPhoneVerification from '../components/RMFPhoneVerification/RMFPhoneVerification';
import RMFPlaceOfBirth from '../components/RMFPlaceOfBirth/RMFPlaceOfBirth';
import RMFStandaloneFinalPage from '../components/RMFStandaloneFinalPage/RMFStandaloneFinalPage';
import RMFSummaryScreen from '../components/RMFSummaryScreen/RMFSummaryScreen';
import RMFVerification from '../components/RMFVerification/RMFVerification';

import useRMFBaseSteps from './useRMFBaseSteps';
import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfDEFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();
  const invokeRmfDobUpdate = new Invoke(Service.RmfDobUpdate, RMFPhone, RMFDOB);

  const forkDobCheck = new Fork(
    Guard.RmfDobEnabled,
    [RMFDOB, invokeRmfDobUpdate, RMFPhone],
    [RMFPhone]
  );
  const invokeSignupDob = new Invoke(Service.SignupDobFetch, forkDobCheck);

  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const { forkSignupDepositCheckDone } = useRMFBaseSteps();

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [forkSignupDepositCheckDone]
  );

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      RMFStandaloneFinalPage,
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [forkJumpToFeature]
  );

  const forkVerification = new Fork(
    Guard.CheckIsDEUserRMOK,
    [RMFVerification, forkRmfExit],
    [forkRmfExit]
  );

  const invokeRetryEnableRealMoneyFlag = new Invoke(
    Service.RmfRetryInvokeRealMoneyOk,
    forkVerification,
    forkRmfExit
  );

  const invokeRetryDEDataSave = new Invoke(
    Service.RmfRetryDEUserDataSave,
    invokeRetryEnableRealMoneyFlag
  );

  const forkSummaryScreen = new Fork(
    Guard.IsRetryScreenEnabled,
    [RMFSummaryScreen, invokeRetryDEDataSave],
    [forkRmfExit]
  );

  const forkRetryScreen = new Fork(
    Guard.CheckIsDEUserRMOK,
    [RMFVerification, forkSignupDepositCheckDone],
    [forkSummaryScreen]
  );

  const invokeEnableRealMoneyFlag = new Invoke(Service.RmfEnableRealMoneyFlag, forkRetryScreen);

  const invokeDepositLimitSave = new Invoke(
    Service.RmfCrossOperatorDepositLimitSave,
    invokeEnableRealMoneyFlag,
    RMFCrossOperatorDepositLimit
  );

  const invokeBirthNameSave = new Invoke(Service.RmfBirthNameSave, RMFAddress, RMFLegalName);
  const invokeLegalNameSave = new Invoke(
    Service.RmfLegalNameSave,
    invokeBirthNameSave,
    RMFLegalName
  );

  const invokeAddressSave = new Invoke(Service.RmfAddressDESave, RMFPlaceOfBirth, RMFAddress);
  const invokePlaceOfBirthSave = new Invoke(
    Service.RmfPlaceOfBirthSave,
    RMFCitizenship,
    RMFPlaceOfBirth
  );

  const invokeCitizenshipSave = new Invoke(
    Service.RmfCitizenshipSave,
    RMFCrossOperatorDepositLimit,
    RMFCitizenship
  );

  const forkDepositLimits = new Fork(Guard.LimitsSelected, invokeEnableRealMoneyFlag, [
    invokeDepositLimitSave,
    invokeEnableRealMoneyFlag
  ]);

  const invokePhoneVerificationSave = new Invoke(
    Service.RmfMobilePhoneVerificationSave,
    RMFLegalName,
    RMFPhoneVerification
  );

  const forkPhoneVerificationCheck = new Fork(
    Guard.PhoneVerificationCheck,
    [RMFPhoneVerification, invokePhoneVerificationSave, RMFLegalName],
    [RMFLegalName]
  );

  const invokeRMFPhoneSave = new Invoke(
    Service.RmfMobilePhoneSaveDefault,
    forkPhoneVerificationCheck,
    RMFPhone
  );

  return [
    invokeSignupDob,
    forkDobCheck,
    invokeRMFPhoneSave,
    forkPhoneVerificationCheck,
    invokeLegalNameSave,
    invokeBirthNameSave,
    RMFAddress,
    invokeAddressSave,
    RMFPlaceOfBirth,
    invokePlaceOfBirthSave,
    RMFCitizenship,
    invokeCitizenshipSave,
    RMFCrossOperatorDepositLimit,
    forkDepositLimits,
    forkRetryScreen,
    invokeRetryEnableRealMoneyFlag,
    jumpToFeatureStep
  ];
};

export default useRmfDEFlow;
